// Domain-specific landing page configuration for secondary domains
// Each domain gets a branded splash page before redirecting to the canonical domain

export interface DomainLanding {
  hostname: string; // without www
  brandName: string;
  tagline: {
    en: string;
    fr: string;
  };
  description: {
    en: string;
    fr: string;
  };
  icon: string; // emoji
  accentColor: string; // tailwind hsl token or raw color
  redirectPath: string; // path on canonical domain
  keywords: string[];
}

const CANONICAL = "https://estate.israelprime.com";

export const CANONICAL_DOMAIN = CANONICAL;

export const domainLandings: DomainLanding[] = [
  {
    hostname: "housetelaviv.com",
    brandName: "House Tel Aviv",
    tagline: {
      en: "Your Gateway to Tel Aviv Homes",
      fr: "Votre porte d'entrée vers les maisons de Tel Aviv",
    },
    description: {
      en: "Discover premium houses, apartments and penthouses in Tel Aviv. Curated by Israel Prime Estate.",
      fr: "Découvrez maisons, appartements et penthouses premium à Tel Aviv. Sélectionnés par Israel Prime Estate.",
    },
    icon: "🏠",
    accentColor: "210 80% 55%",
    redirectPath: "/properties",
    keywords: ["house tel aviv", "maison tel aviv", "buy home tel aviv"],
  },
  {
    hostname: "penthouseisrael.com",
    brandName: "Penthouse Israel",
    tagline: {
      en: "Exclusive Penthouses Across Israel",
      fr: "Penthouses exclusifs à travers Israël",
    },
    description: {
      en: "Access the most prestigious penthouse listings in Tel Aviv, Jerusalem, Herzliya and beyond.",
      fr: "Accédez aux penthouses les plus prestigieux de Tel Aviv, Jérusalem, Herzliya et au-delà.",
    },
    icon: "🏙️",
    accentColor: "270 60% 50%",
    redirectPath: "/properties",
    keywords: ["penthouse israel", "penthouse tel aviv", "penthouse jerusalem"],
  },
  {
    hostname: "apartmenttelaviv.com",
    brandName: "Apartment Tel Aviv",
    tagline: {
      en: "Find Your Perfect Tel Aviv Apartment",
      fr: "Trouvez votre appartement idéal à Tel Aviv",
    },
    description: {
      en: "Browse luxury apartments for sale and rent in Tel Aviv's most sought-after neighborhoods.",
      fr: "Parcourez les appartements de luxe à vendre et à louer dans les quartiers les plus prisés de Tel Aviv.",
    },
    icon: "🏢",
    accentColor: "200 70% 50%",
    redirectPath: "/properties",
    keywords: ["apartment tel aviv", "appartement tel aviv", "flat tel aviv"],
  },
  {
    hostname: "realestatetlv.com",
    brandName: "Real Estate TLV",
    tagline: {
      en: "Tel Aviv Real Estate Experts",
      fr: "Experts immobiliers de Tel Aviv",
    },
    description: {
      en: "Your trusted real estate platform for buying, selling and investing in Tel Aviv properties.",
      fr: "Votre plateforme immobilière de confiance pour acheter, vendre et investir à Tel Aviv.",
    },
    icon: "🔑",
    accentColor: "160 60% 45%",
    redirectPath: "/",
    keywords: ["real estate tel aviv", "immobilier tel aviv", "property tlv"],
  },
  {
    hostname: "telavivimmobilier.com",
    brandName: "Tel Aviv Immobilier",
    tagline: {
      en: "French-Speaking Real Estate in Tel Aviv",
      fr: "L'immobilier francophone à Tel Aviv",
    },
    description: {
      en: "Premium real estate services in Tel Aviv for French-speaking buyers and investors.",
      fr: "Services immobiliers premium à Tel Aviv pour les acheteurs et investisseurs francophones.",
    },
    icon: "🇫🇷",
    accentColor: "220 70% 50%",
    redirectPath: "/",
    keywords: ["immobilier tel aviv", "agence immobilière tel aviv", "achat tel aviv"],
  },
  {
    hostname: "israelprimeestate.com",
    brandName: "Israel Prime Estate",
    tagline: {
      en: "Premier Luxury Real Estate in Israel",
      fr: "Immobilier de luxe de premier plan en Israël",
    },
    description: {
      en: "Israel Prime Estate — your trusted partner for premium real estate investments across Israel.",
      fr: "Israel Prime Estate — votre partenaire de confiance pour l'investissement immobilier premium en Israël.",
    },
    icon: "💎",
    accentColor: "45 80% 50%",
    redirectPath: "/",
    keywords: ["israel prime estate", "luxury real estate israel", "immobilier luxe israel"],
  },
  {
    hostname: "penthousetelaviv.com",
    brandName: "Penthouse Tel Aviv",
    tagline: {
      en: "Luxury Penthouses in Tel Aviv",
      fr: "Penthouses de luxe à Tel Aviv",
    },
    description: {
      en: "Explore exclusive penthouses with sea views, rooftop terraces and private pools in Tel Aviv.",
      fr: "Explorez des penthouses exclusifs avec vue mer, terrasses et piscines privées à Tel Aviv.",
    },
    icon: "✨",
    accentColor: "280 55% 55%",
    redirectPath: "/properties",
    keywords: ["penthouse tel aviv", "luxury penthouse tlv", "penthouse vue mer tel aviv"],
  },
  {
    hostname: "israel-prime.com",
    brandName: "Israel Prime",
    tagline: {
      en: "Prime Real Estate Investments in Israel",
      fr: "Investissements immobiliers de premier choix en Israël",
    },
    description: {
      en: "Access off-market properties and exclusive investment opportunities across Israel.",
      fr: "Accédez à des biens hors marché et des opportunités d'investissement exclusives en Israël.",
    },
    icon: "🌟",
    accentColor: "35 75% 50%",
    redirectPath: "/",
    keywords: ["israel prime", "real estate investment israel", "investissement immobilier israel"],
  },
  {
    hostname: "luxuryisrael.com",
    brandName: "Luxury Israel",
    tagline: {
      en: "Ultra-Luxury Properties in Israel",
      fr: "Propriétés ultra-luxe en Israël",
    },
    description: {
      en: "Discover the finest luxury villas, penthouses and estates across Israel's most exclusive addresses.",
      fr: "Découvrez les plus belles villas, penthouses et propriétés d'exception aux adresses les plus exclusives d'Israël.",
    },
    icon: "👑",
    accentColor: "45 90% 45%",
    redirectPath: "/properties",
    keywords: ["luxury israel", "villa luxe israel", "luxury property israel"],
  },
  {
    hostname: "herzliyarealestate.com",
    brandName: "Herzliya Real Estate",
    tagline: {
      en: "Premium Properties in Herzliya Pituach",
      fr: "Propriétés premium à Herzliya Pitouah",
    },
    description: {
      en: "Explore exclusive beachfront villas and luxury apartments in Herzliya Pituach, Israel's Riviera.",
      fr: "Explorez villas en bord de mer et appartements de luxe à Herzliya Pitouah, la Riviera d'Israël.",
    },
    icon: "🏖️",
    accentColor: "190 65% 50%",
    redirectPath: "/properties",
    keywords: ["herzliya real estate", "herzliya pituach", "immobilier herzliya"],
  },
  {
    hostname: "jerusalemrealestate.com",
    brandName: "Jerusalem Real Estate",
    tagline: {
      en: "Prestigious Properties in Jerusalem",
      fr: "Propriétés prestigieuses à Jérusalem",
    },
    description: {
      en: "Find exceptional homes and investment properties in Jerusalem's most desirable neighborhoods.",
      fr: "Trouvez des biens d'exception et d'investissement dans les quartiers les plus prisés de Jérusalem.",
    },
    icon: "🕌",
    accentColor: "40 65% 50%",
    redirectPath: "/properties",
    keywords: ["jerusalem real estate", "immobilier jerusalem", "property jerusalem"],
  },
  {
    hostname: "netanyarealestate.com",
    brandName: "Netanya Real Estate",
    tagline: {
      en: "Beachfront Living in Netanya",
      fr: "Vivre en bord de mer à Netanya",
    },
    description: {
      en: "Discover sea-view apartments and new construction projects in Netanya's vibrant coastal city.",
      fr: "Découvrez appartements vue mer et projets neufs dans la ville côtière dynamique de Netanya.",
    },
    icon: "🌊",
    accentColor: "195 70% 50%",
    redirectPath: "/properties",
    keywords: ["netanya real estate", "immobilier netanya", "apartment netanya"],
  },
  {
    hostname: "investisraelproperty.com",
    brandName: "Invest Israel Property",
    tagline: {
      en: "Smart Real Estate Investment in Israel",
      fr: "Investissement immobilier intelligent en Israël",
    },
    description: {
      en: "Data-driven property investment opportunities with high rental yields across Israel.",
      fr: "Opportunités d'investissement immobilier basées sur les données avec des rendements locatifs élevés en Israël.",
    },
    icon: "📈",
    accentColor: "150 60% 45%",
    redirectPath: "/",
    keywords: ["invest israel property", "israel property investment", "investir immobilier israel"],
  },
  {
    hostname: "villastelaviv.com",
    brandName: "Villas Tel Aviv",
    tagline: {
      en: "Exclusive Villas in Tel Aviv & Surroundings",
      fr: "Villas exclusives à Tel Aviv et environs",
    },
    description: {
      en: "Browse luxury villas with private gardens and pools in Tel Aviv, Herzliya and the Sharon region.",
      fr: "Parcourez les villas de luxe avec jardins et piscines privés à Tel Aviv, Herzliya et la région du Sharon.",
    },
    icon: "🏡",
    accentColor: "120 50% 45%",
    redirectPath: "/properties",
    keywords: ["villa tel aviv", "villas israel", "villa luxe tel aviv"],
  },
  {
    hostname: "immobilierisrael.com",
    brandName: "Immobilier Israël",
    tagline: {
      en: "French-Speaking Real Estate Agency in Israel",
      fr: "Agence immobilière francophone en Israël",
    },
    description: {
      en: "Full-service French-speaking real estate agency for buying, selling and investing in Israel.",
      fr: "Agence immobilière francophone complète pour acheter, vendre et investir en Israël.",
    },
    icon: "🇮🇱",
    accentColor: "215 65% 50%",
    redirectPath: "/",
    keywords: ["immobilier israel", "agence immobilière israel", "achat immobilier israel"],
  },
  {
    hostname: "telavivapartments.com",
    brandName: "Tel Aviv Apartments",
    tagline: {
      en: "Curated Apartments for Sale in Tel Aviv",
      fr: "Appartements sélectionnés à vendre à Tel Aviv",
    },
    description: {
      en: "Hand-picked apartments in Neve Tzedek, Rothschild, Old North and all prime Tel Aviv neighborhoods.",
      fr: "Appartements triés sur le volet à Neve Tsedek, Rothschild, Old North et tous les quartiers prisés de Tel Aviv.",
    },
    icon: "🔑",
    accentColor: "25 70% 50%",
    redirectPath: "/properties",
    keywords: ["tel aviv apartments", "apartment for sale tel aviv", "appartement tel aviv vente"],
  },
  {
    hostname: "israelprimeproperties.com",
    brandName: "Israel Prime Properties",
    tagline: {
      en: "The Premier Property Platform in Israel",
      fr: "La plateforme immobilière de référence en Israël",
    },
    description: {
      en: "Israel Prime Properties — comprehensive listings of luxury homes, new builds and investment opportunities.",
      fr: "Israel Prime Properties — annonces complètes de résidences de luxe, constructions neuves et opportunités d'investissement.",
    },
    icon: "🏛️",
    accentColor: "250 55% 50%",
    redirectPath: "/",
    keywords: ["israel prime properties", "israel luxury properties", "propriétés israel"],
  },
];

/**
 * Find landing config by hostname (handles www prefix)
 */
export const findDomainLanding = (hostname: string): DomainLanding | null => {
  const clean = hostname.replace(/^www\./, "").toLowerCase();
  // Don't match canonical or staging domains
  if (
    clean === "estate.israelprime.com" ||
    clean.endsWith(".lovable.app") ||
    clean === "localhost"
  ) {
    return null;
  }
  return domainLandings.find((d) => d.hostname === clean) || null;
};
